import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactElement } from 'react';
import type { RecentMainCommandChat } from '../lib/main-command-chat-archive';
import type {
  MainCommandDirectRecipient,
  MainCommandReplyIdentity,
  MainCommandReplyTarget,
} from '../lib/main-command-recipients';
import { sendsOnEnter } from '../lib/composing';
import {
  FLOATING_MAIN_COMMAND_DEFAULT_SIZE,
  browserFloatingMainCommandViewport,
  clampFloatingMainCommandSize,
  loadFloatingMainCommandSize,
  persistFloatingMainCommandSize,
  resetFloatingMainCommandSize,
} from '../lib/floating-main-command-size';
import { appendDictation } from '../lib/speech';
import type { SharedTimelineItem } from '../lib/shared-command-timeline';
import type { WorkerChatReadiness } from '../lib/worker-chat-readiness';
import {
  boundedMiniTimeline,
  miniCanSend,
  miniConversationStateLabel,
} from '../lib/main-command-mini';
import { ReplyTargetBar } from './ReplyTargetBar';
import { DictateButton } from './Voice';
import { MessageTimestamp } from './MessageTimestamp';
import { MainCommandReceipt } from './MainCommandReceipt';
import { ControlReceiptNote } from './ControlReceiptNote';
import { SENT_MESSAGE, STEERING_MESSAGE, type ControlReceiptView } from '../lib/control-receipt';
import { ChatText } from './ChatText';
import { MainCommandMentionMenu } from './MainCommandMentionMenu';
import {
  mainCommandMentionSuggestions,
  removeSelectedMainCommandMention,
  type MainCommandMentionCandidate,
} from '../lib/main-command-mentions';
import '../floating-main-command.css';

export interface FloatingMainCommandProps {
  readonly expanded: boolean;
  readonly onExpandedChange: (expanded: boolean) => void;
  readonly activeConversation: RecentMainCommandChat | null;
  /** The exact canonical visible timeline slice for the active #493 conversation. */
  readonly timeline: readonly SharedTimelineItem[];
  readonly unreadCount: number;
  /** Canonical owner/Management Attention count supplied by the host. */
  readonly managementAttentionCount: number;
  readonly onOpenManagementAttention: () => void;
  /** The canonical management targets. The picker offers the Overseer only; the two Observers are history. */
  readonly selectedParticipants: ReadonlySet<MainCommandReplyTarget>;
  readonly identities: Readonly<Record<MainCommandReplyTarget, MainCommandReplyIdentity>>;
  readonly onToggleParticipant: (target: MainCommandReplyTarget) => void;
  /**
   * Canonical #531 Worker participants and stable Worker-id selection.
   * #562 owns active-usable filtering upstream; this presentation must not infer availability.
   */
  readonly workers: readonly WorkerChatReadiness[];
  readonly eligibleWorkerIds: ReadonlySet<string>;
  readonly selectedWorkers: ReadonlySet<string>;
  readonly onToggleWorker: (workerId: string) => void;
  readonly onToggleAllWorkers: () => void;
  readonly mentionCandidates: readonly MainCommandMentionCandidate[];
  readonly onMentionSelect: (candidate: MainCommandMentionCandidate) => void;
  readonly onDirectReply: (recipient: MainCommandDirectRecipient) => void;
  /** Shared/canonical composer draft. The mini owns no parallel draft store. */
  readonly draft: string;
  readonly onDraftChange: (draft: string) => void;
  /** Same guarded Main Command send authority as the full composer. */
  readonly onSend: () => void | Promise<void>;
  readonly sending?: boolean;
  readonly commandReceipt?: ControlReceiptView | null;
  readonly controlReceipts?: ReadonlyMap<string, ControlReceiptView>;
  /** What each sent message became ("New job for Worker 3"), by command; steering when Brunel did not say. */
  readonly controlReceiptActions?: ReadonlyMap<string, string>;
  readonly onOpenFullCommand: () => void;
}

export function FloatingMainCommand({
  expanded,
  onExpandedChange,
  activeConversation,
  timeline,
  unreadCount,
  managementAttentionCount,
  onOpenManagementAttention,
  selectedParticipants,
  identities,
  onToggleParticipant,
  workers,
  eligibleWorkerIds,
  selectedWorkers,
  onToggleWorker,
  onToggleAllWorkers,
  mentionCandidates,
  onMentionSelect,
  onDirectReply,
  draft,
  onDraftChange,
  onSend,
  sending = false,
  commandReceipt = null,
  controlReceipts,
  controlReceiptActions,
  onOpenFullCommand,
}: FloatingMainCommandProps): ReactElement {
  const panel = useRef<HTMLElement | null>(null);
  const composer = useRef<HTMLTextAreaElement | null>(null);
  const [size, setSize] = useState(FLOATING_MAIN_COMMAND_DEFAULT_SIZE);
  const [mentionCaret, setMentionCaret] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionSuggestions = useMemo(
    () => mainCommandMentionSuggestions(draft, mentionCaret, mentionCandidates),
    [draft, mentionCaret, mentionCandidates],
  );
  const recent = boundedMiniTimeline(timeline);
  const readOnly = activeConversation?.kind === 'archived';
  const canSend = miniCanSend({
    activeConversation,
    selectedParticipants,
    selectedWorkers,
    draft,
    sending,
  });
  const title = activeConversation?.title?.trim() || 'Main Command';

  useEffect(() => {
    if (!expanded) return;
    const viewport = browserFloatingMainCommandViewport();
    if (viewport === null) return;
    setSize(loadFloatingMainCommandSize(viewport));
  }, [expanded]);

  useEffect(() => {
    if (!expanded || typeof window === 'undefined') return undefined;
    const onViewportResize = () => {
      const viewport = browserFloatingMainCommandViewport();
      if (viewport === null) return;
      setSize((current) => clampFloatingMainCommandSize(current, viewport));
    };
    window.addEventListener('resize', onViewportResize);
    return () => window.removeEventListener('resize', onViewportResize);
  }, [expanded]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSend) return;
    void onSend();
  };

  const capturePanelSize = () => {
    if (!expanded || panel.current === null) return;
    const viewport = browserFloatingMainCommandViewport();
    if (viewport === null) return;
    const bounds = panel.current.getBoundingClientRect();
    const next = clampFloatingMainCommandSize({
      width: bounds.width,
      height: bounds.height,
    }, viewport);
    setSize(next);
    persistFloatingMainCommandSize(next);
  };

  const resetPanelSize = () => {
    const viewport = browserFloatingMainCommandViewport();
    if (viewport === null) {
      setSize(FLOATING_MAIN_COMMAND_DEFAULT_SIZE);
      return;
    }
    setSize(resetFloatingMainCommandSize(viewport));
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionSuggestions.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        setMentionIndex((current) => (current + direction + mentionSuggestions.length) % mentionSuggestions.length);
        return;
      }
      if ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') {
        event.preventDefault();
        const candidate = mentionSuggestions[Math.min(mentionIndex, mentionSuggestions.length - 1)]!;
        const next = removeSelectedMainCommandMention(draft, event.currentTarget.selectionStart);
        onMentionSelect(candidate);
        onDraftChange(next.content);
        setMentionCaret(next.caret);
        setMentionIndex(0);
        queueMicrotask(() => composer.current?.setSelectionRange(next.caret, next.caret));
        return;
      }
    }
    if (!sendsOnEnter({
      key: event.key,
      shiftKey: event.shiftKey,
      isComposing: event.nativeEvent.isComposing,
      altKey: event.altKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
    })) return;
    event.preventDefault();
    if (canSend) void onSend();
  };

  return (
    <section
      ref={panel}
      className={expanded ? 'floating-main-command is-expanded' : 'floating-main-command'}
      aria-label="Mini Main Command"
      data-testid="floating-main-command"
      style={expanded ? { width: size.width, height: size.height } : undefined}
      onPointerUp={(event) => {
        if (event.target === event.currentTarget) capturePanelSize();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !expanded) return;
        event.stopPropagation();
        onExpandedChange(false);
      }}
    >
      <header className="floating-main-command-head">
        <button
          type="button"
          className="floating-main-command-toggle"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse mini Main Command' : 'Expand mini Main Command'}
          title={expanded ? 'Collapse mini Main Command' : 'Expand mini Main Command'}
          onClick={() => onExpandedChange(!expanded)}
        >
          <span className="floating-main-command-mark" aria-hidden="true">MC</span>
          <span className="floating-main-command-title">
            <strong>{title}</strong>
            <small>{miniConversationStateLabel(activeConversation)}</small>
          </span>
          {(unreadCount > 0 || managementAttentionCount > 0) && (
            <span className="floating-main-command-badges" aria-label="Main Command status">
              {unreadCount > 0 && (
                <span className="floating-main-command-badge" aria-label={`${unreadCount} unread`}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              {managementAttentionCount > 0 && (
                <span className="floating-main-command-badge attention" aria-label={`${managementAttentionCount} management attention`}>
                  {managementAttentionCount > 99 ? '99+' : managementAttentionCount}
                </span>
              )}
            </span>
          )}
        </button>
      </header>

      {expanded && (
        <div className="floating-main-command-body">
          <div className="floating-main-command-actions">
            <button type="button" className="ghost" onClick={onOpenManagementAttention}>
              Management attention{managementAttentionCount > 0 ? ` ${managementAttentionCount}` : ''}
            </button>
            <button type="button" className="ghost" onClick={onOpenFullCommand}>
              Open full Command Center
            </button>
            <button
              type="button"
              className="ghost floating-main-command-size-reset"
              title="Restore the default floating Main Command size"
              onPointerUp={(event) => event.stopPropagation()}
              onClick={resetPanelSize}
            >
              Reset size
            </button>
          </div>

          <div className="floating-main-command-transcript" aria-label="Recent Main Command conversation">
            {recent.length === 0 ? (
              <p className="muted">No messages in this conversation yet.</p>
            ) : recent.map((item) => (
              <article
                key={item.id}
                className={item.mine ? 'floating-main-command-turn mine' : 'floating-main-command-turn'}
                data-authority={item.authority}
                data-role={item.role}
              >
                <small className="muted">
                  {item.replyRecipient ? (
                    <button type="button" className="participant-byline" title={`Reply to ${item.byline}`}
                      aria-label={`Reply to ${item.byline}`} onClick={() => {
                        onDirectReply(item.replyRecipient!);
                        queueMicrotask(() => composer.current?.focus());
                      }}>
                      {item.byline}
                    </button>
                  ) : item.byline}
                  <MessageTimestamp sentAt={item.sentAt} />
                </small>
                {item.mine && ((item.targetRoles?.length ?? 0) > 0 || (item.targetWorkerLabels?.length ?? 0) > 0) && (
                  <span className="command-target-badges" aria-label="Reply targets">
                    {item.targetRoles.map((target) => (
                      <span className="tag" key={target} data-reply-target={target}>
                        {target === 'overseer' ? 'Overseer' : target === 'ux' ? 'UX' : 'Core'}
                      </span>
                    ))}
                    {item.targetWorkerLabels.map((label, index) => (
                      <span className="tag" key={`worker:${label}:${index}`} data-reply-target="worker">{label}</span>
                    ))}
                  </span>
                )}
                {item.mine ? <p>{item.text}</p> : <ChatText text={item.text} />}
                {item.mine && (item.recipientStatuses?.length ?? 0) > 0 && (
                  <MainCommandReceipt statuses={item.recipientStatuses!} />
                )}
                {item.mine && item.commandTurn !== null && controlReceipts?.get(item.commandTurn.id) !== undefined && (
                  <ControlReceiptNote view={controlReceipts.get(item.commandTurn.id)!}
                    action={controlReceiptActions?.get(item.commandTurn.id) ?? STEERING_MESSAGE} />
                )}
              </article>
            ))}
          </div>

          {readOnly ? (
            <p className="floating-main-command-readonly">
              Archived conversation · read only. Open full Command Center to Continue this chat.
            </p>
          ) : (
            <>
              <ReplyTargetBar
                selected={selectedParticipants}
                identities={identities}
                onToggle={onToggleParticipant}
                workers={workers}
                eligibleWorkerIds={eligibleWorkerIds}
                selectedWorkers={selectedWorkers}
                onToggleWorker={onToggleWorker}
                onToggleAllWorkers={onToggleAllWorkers}
              />
              <form className="floating-main-command-composer" onSubmit={submit} data-reload-preserved="main-command">
                <textarea
                  ref={composer}
                  className="command-box"
                  aria-label="Message Main Command"
                  placeholder="Message Main Command"
                  value={draft}
                  onChange={(event) => {
                    onDraftChange(event.target.value);
                    setMentionCaret(event.target.selectionStart);
                    setMentionIndex(0);
                  }}
                  onClick={(event) => setMentionCaret(event.currentTarget.selectionStart)}
                  onKeyUp={(event) => {
                    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') setMentionCaret(event.currentTarget.selectionStart);
                  }}
                  onKeyDown={onComposerKeyDown}
                  maxLength={16000}
                />
                <MainCommandMentionMenu
                  suggestions={mentionSuggestions}
                  activeIndex={Math.min(mentionIndex, Math.max(0, mentionSuggestions.length - 1))}
                  onChoose={(candidate) => {
                    const next = removeSelectedMainCommandMention(draft, mentionCaret);
                    onMentionSelect(candidate);
                    onDraftChange(next.content);
                    setMentionCaret(next.caret);
                    setMentionIndex(0);
                    queueMicrotask(() => {
                      composer.current?.focus();
                      composer.current?.setSelectionRange(next.caret, next.caret);
                    });
                  }}
                />
                <div className="floating-main-command-composer-actions">
                  <DictateButton
                    onText={(text) => onDraftChange(appendDictation(draft, text).slice(0, 16000))}
                  />
                  <button type="submit" className="primary" disabled={!canSend}>
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {commandReceipt !== null && <ControlReceiptNote view={commandReceipt} action={SENT_MESSAGE} />}
              </form>
            </>
          )}
        </div>
      )}
    </section>
  );
}
